import type { LcSwipeableInstance, LcSwipeableSide } from '@lottocom/frontend-components'

import { addYears } from 'date-fns'

import {
  EDITOR_TUTORIAL_COOKIE_MAX_AGE_YEARS,
  EDITOR_TUTORIAL_COOKIE_NAME,
  EDITOR_TUTORIAL_OVERLAY_FADE_MS,
  EDITOR_TUTORIAL_SWIPE_BACK_MS,
  EDITOR_TUTORIAL_SWIPE_BEAT_MS,
  EDITOR_TUTORIAL_SWIPE_DISTANCE_PX,
  EDITOR_TUTORIAL_SWIPE_HOLD_MS,
  EDITOR_TUTORIAL_SWIPE_OUT_MS,
} from '../constants/tutorial'

export type EditorTutorialStep = 'clear' | 'done' | 'idle' | 'opening' | 'quickPick'

type SwipeableSource = () => Maybe<LcSwipeableInstance>

const SWIPE_DURATION_MS =
  EDITOR_TUTORIAL_SWIPE_OUT_MS + EDITOR_TUTORIAL_SWIPE_HOLD_MS + EDITOR_TUTORIAL_SWIPE_BACK_MS

const step = ref<EditorTutorialStep>('idle')
const targetPlayId = ref<Maybe<string>>(null)
const isCaptionVisible = ref(false)
const timers = new Set<ReturnType<typeof setTimeout>>()

let runToken = 0

export const useEditorTutorial = () => {
  const hasSeen = useCookie(EDITOR_TUTORIAL_COOKIE_NAME, {
    default: () => false,
    expires: addYears(new Date(), EDITOR_TUTORIAL_COOKIE_MAX_AGE_YEARS),
  })

  const isActive = computed(() => step.value !== 'idle')

  const isFinished = computed(() => step.value === 'done')

  const clearTimers = () => {
    timers.forEach((timer) => clearTimeout(timer))
    timers.clear()
  }

  const wait = (ms: number) =>
    new Promise<void>((resolve) => {
      const timer = setTimeout(() => {
        timers.delete(timer)
        resolve()
      }, ms)
      timers.add(timer)
    })

  const stop = () => {
    runToken += 1
    clearTimers()
    step.value = 'idle'
    targetPlayId.value = null
    isCaptionVisible.value = false
  }

  const finish = () => {
    hasSeen.value = true
    stop()
  }

  const abort = (playId?: string) => {
    if (playId && playId !== targetPlayId.value) return
    stop()
  }

  const start = async (playId: string, getSwipeable: SwipeableSource) => {
    if (hasSeen.value || isActive.value) return

    const token = ++runToken
    const isCurrentRun = () => token === runToken

    const slide = (side: LcSwipeableSide) =>
      getSwipeable()?.preview?.(side, {
        distance: EDITOR_TUTORIAL_SWIPE_DISTANCE_PX,
        hold: EDITOR_TUTORIAL_SWIPE_HOLD_MS,
        slideBack: EDITOR_TUTORIAL_SWIPE_BACK_MS,
        slideOut: EDITOR_TUTORIAL_SWIPE_OUT_MS,
      }) ?? wait(SWIPE_DURATION_MS)

    const reveal = async (side: LcSwipeableSide) => {
      const sliding = slide(side)

      isCaptionVisible.value = true
      await wait(EDITOR_TUTORIAL_SWIPE_OUT_MS + EDITOR_TUTORIAL_SWIPE_HOLD_MS)
      if (!isCurrentRun()) return

      isCaptionVisible.value = false
      await sliding
    }

    targetPlayId.value = playId
    step.value = 'opening'

    await wait(EDITOR_TUTORIAL_OVERLAY_FADE_MS)
    if (!isCurrentRun()) return

    step.value = 'clear'
    await reveal('left')
    if (!isCurrentRun()) return

    await wait(EDITOR_TUTORIAL_SWIPE_BEAT_MS)
    if (!isCurrentRun()) return

    step.value = 'quickPick'
    await reveal('right')
    if (!isCurrentRun()) return

    await wait(EDITOR_TUTORIAL_SWIPE_BEAT_MS)
    if (!isCurrentRun()) return

    hasSeen.value = true
    step.value = 'done'
  }

  return {
    abort,
    finish,
    hasSeen,
    isActive,
    isCaptionVisible: readonly(isCaptionVisible),
    isFinished,
    start,
    step: readonly(step),
    targetPlayId: readonly(targetPlayId),
  }
}
